// Company Profile: where each section shows on the site, and its built-in items
// (plan R6 CE-2). Until a section's built-in items are imported, the page shows
// them FIRST and the items added here after them, so the arrows only order the
// tail. "Import the built-in items" (admin / super-admin) copies them into the
// list once, in page order; from then on the page shows this list only, and
// every item can be ordered, edited and hidden.
import { Box, Button, Card, Link, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import Label from 'src/components/Label';
import { siteBase } from 'src/utils/siteUrl';

export type CompanySection = 'partner' | 'diploma' | 'company' | 'story';

// The built-in items of the site (fe fab86b0), the same lists the API copies.
export const BUILT_IN_COUNT: Record<CompanySection, number> = {
  partner: 28,
  story: 7,
  diploma: 6,
  company: 3,
};

// The public page each section appears on.
export const SECTION_PAGE: Record<CompanySection, { path: string; name: string }> = {
  partner: { path: '/company-profile#partners', name: 'Company profile — Our success partners' },
  story: { path: '/company-profile#success', name: 'Company profile — Success stories' },
  diploma: { path: '/company-profile/learners', name: 'Kayfa for Learners' },
  company: { path: '/company-profile/companies', name: 'Kayfa for Companies' },
};

export const sectionPageUrl = (section: CompanySection) => `${siteBase()}${SECTION_PAGE[section].path}`;

/** The note under a section until its built-in items are imported. */
export const notImportedText = (section: CompanySection) =>
  `Items you add here show AFTER the page's ${BUILT_IN_COUNT[section]} built-in items. Import the built-in items to order everything.`;

function PageLink({ section }: { section: CompanySection }) {
  return (
    <Link
      href={sectionPageUrl(section)}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {SECTION_PAGE[section].name}
      <Iconify icon="eva:external-link-outline" sx={{ width: 16, height: 16 }} />
    </Link>
  );
}

type Props = {
  // null = "All sections".
  section: CompanySection | null;
  sections: { value: CompanySection; label: string }[];
  imported: Record<CompanySection, boolean>;
  canImport: boolean;
  importing: boolean;
  onImport: (section: CompanySection) => void;
};

export default function BuiltInsCard({ section, sections, imported, canImport, importing, onImport }: Props) {
  if (!section) {
    return (
      <Card sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Where each section shows
        </Typography>
        <Stack spacing={1}>
          {sections.map((s) => (
            <Stack key={s.value} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Box sx={{ minWidth: 160 }}>
                <Typography variant="body2">{s.label}</Typography>
              </Box>
              <PageLink section={s.value} />
              <Label color={imported[s.value] ? 'success' : 'default'}>
                {imported[s.value] ? 'Built-in items imported' : `${BUILT_IN_COUNT[s.value]} built-in items first`}
              </Label>
            </Stack>
          ))}
        </Stack>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2.5, mb: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            Shows on:
          </Typography>
          <PageLink section={section} />
        </Stack>
        {imported[section] ? (
          <Typography variant="body2" color="text.secondary">
            The page shows exactly this list, in this order. The built-in items are part of it: order,
            edit or hide them like any other item (they cannot be deleted).
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              {notImportedText(section)}
            </Typography>
            {canImport ? (
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={importing}
                  onClick={() => onImport(section)}
                  startIcon={<Iconify icon="eva:download-outline" />}
                >
                  {importing ? 'Importing…' : 'Import the built-in items'}
                </Button>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                An admin can import them.
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}
